import { Button, Form, Modal, Toast } from '@seiue/ui'
import { isEmpty, omit } from 'lodash'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { GrowpIcon } from 'packages/components/GrowpIcon'
import {
  InformalReflection,
  informalReflectionApi,
} from 'packages/sdks-next/growp'
import { EntryComponentsWrapper } from 'packages/theme'
import { AnyType, AnyValueObj } from 'packages/types'

import { getTheme } from '@/utils/theme'

import { ViewForm } from './components/ViewForm'
import { useUserDetailContext } from './context'
import { IConfig } from './types'
import { formatPrivateInfo } from '@/features/activities/pages/Detail/ReflectionList/utils'
const theme = getTheme()

/**
 * 除固定信息的其他信息的表单
 *
 * @param props - 参数
 * @param props.configs - 表单配置项
 * @param props.data - 表单初始值
 * @param props.groupName - 分组名称
 * @param props.initFields - 初始数据
 * @returns 表单
 */
export const OtherInfo: React.FC<{
  configs: IConfig[]
  data: InformalReflection & {
    'guardians.0.id'?: number
    'guardians.1.id'?: number
  }
  groupName: string
  initFields: object
}> = (props) => {
  const { id, updateInfo } = useUserDetailContext()
  const { configs, data, groupName, initFields } = props
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [form] = Form.useForm()

  useEffect(() => {
    // 先清空再赋值，因为某些字段可能不存在而导致取消编辑后数据未更新
    form.resetFields()
    form.setFieldsValue({
      ...data,
      idcard: isEdit ? data.idcard : formatPrivateInfo(data.idcard, true, true),
      account: isEdit
        ? data.account
        : formatPrivateInfo(data.account, true, true),
    })
  }, [isEdit, data])

  const valuesIntegrate: {
    [key: string]: (values: AnyValueObj) => AnyValueObj
  } = {
    基本信息: (values: AnyValueObj) => {
      const fixedBaseInfoKeys = [
        'email',
        'idcard',
        'idcardType',
        'usin',
        'account',
      ]
      const fieldsInfo = omit(values, fixedBaseInfoKeys)
      return {
        ...values,
        fields: {
          ...initFields,
          ...fieldsInfo,
        },
      }
    },
    家庭信息: (values: AnyValueObj) => {
      const guardians0: AnyValueObj = {}
      const guardians1: AnyValueObj = {}
      let IsGuardians0Empty = true
      let IsGuardians1Empty = true
      const finalGuardians: AnyValueObj[] = []
      for (const key in values) {
        const k = key.slice(key.lastIndexOf('.') + 1)
        if (key.includes('.0.')) {
          if (!isEmpty(values[key])) {
            IsGuardians0Empty = false
          }
          if (['phone', 'name', 'pupilType'].includes(k)) {
            guardians0[k] = values[key]
          } else {
            guardians0['fields'] = {
              [k]: values[key],
              ...guardians0['fields'],
            }
          }
        } else if (key.includes('.1.')) {
          if (!isEmpty(values[key])) {
            IsGuardians1Empty = false
          }
          if (['phone', 'name', 'pupilType'].includes(k)) {
            guardians1[k] = values[key]
          } else {
            guardians1['fields'] = {
              [k]: values[key],
              ...guardians1['fields'],
            }
          }
        }
      }

      if (IsGuardians0Empty) {
        guardians0['fields'] = undefined
        if (data['guardians.0.id']) {
          guardians0['removed'] = true
          guardians0['deleted'] = true
          guardians0['id'] = data['guardians.0.id']
          finalGuardians.push({ ...guardians0, guardianLevel: 0 })
        } else {
          const isEmpty = Object.values(guardians0).every(
            (i) => i === undefined
          )
          !isEmpty && finalGuardians.push({ ...guardians0 })
        }
      } else {
        if (data['guardians.0.id']) {
          guardians0['id'] = data['guardians.0.id']
        }
        finalGuardians.push({ ...guardians0, guardianLevel: 0 })
      }

      if (IsGuardians1Empty) {
        guardians1['fields'] = undefined
        if (data['guardians.1.id']) {
          guardians1['removed'] = true
          guardians1['deleted'] = true
          guardians1['id'] = data['guardians.1.id']
          finalGuardians.push({ ...guardians1, guardianLevel: 1 })
        } else {
          const isEmpty = Object.values(guardians1).every(
            (i) => i === undefined
          )
          !isEmpty && finalGuardians.push({ ...guardians1 })
        }
      } else {
        if (data['guardians.1.id']) {
          guardians1['id'] = data['guardians.1.id']
        }
        finalGuardians.push({ ...guardians1, guardianLevel: 1 })
      }

      return {
        fields: {
          ...initFields,
        },
        guardians: finalGuardians,
      }
    },
  }

  const saveInfo = async (values: AnyType) => {
    let finalValues = values

    if (valuesIntegrate[groupName]) {
      finalValues = valuesIntegrate[groupName](values)
    } else {
      finalValues = {
        fields: {
          ...initFields,
          ...values,
        },
      }
    }
    const fds = finalValues['fields']

    const payload = {
      ...finalValues,
      fields: fds,
    }

    if (!updateInfo) return
    await updateInfo({
      id: id,
      payload,
    })

    Toast.success('保存成功')
    setIsEdit(false)
  }

  const onSaveBaseInfo = () => {
    form.validateFields().then(async (values) => {
      const firstPhone = values['guardians.0.phone']
      const secondPhone = values['guardians.1.phone']

      if (firstPhone || secondPhone) {
        const { data } = await informalReflectionApi.loadInformalSamePhone({
          phoneIn: `${firstPhone},${secondPhone}`,
        })
        if (data?.repeat) {
          Modal.confirm({
            title: (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: theme.text._1,
                }}
              >
                <GrowpIcon
                  name="WarnFill"
                  fill="#FF8800"
                  size={22}
                />
                &nbsp;提示！
              </div>
            ),
            content: data?.msg || '未知错误',
            onOk: async () => {
              saveInfo(values)
            },
          })
          return
        }
      }
      saveInfo(values)
    })
  }

  return (
    <EntryComponentsWrapper>
      <BaseInfoWrapper>
        <StyledForm
          form={form}
          labelAlign="right"
          labelCol={{ span: 5, offset: 0 }}
          colon={false}
        >
          <ViewForm
            isEdit={isEdit}
            configs={configs}
            initData={[]}
            viewNotForm={false}
            form={form}
          />
          {configs.length ? (
            <Form.Item wrapperCol={{ span: 24, offset: 3 }}>
              <BtnsWrapper>
                <Button
                  type="primary"
                  onClick={() => {
                    if (isEdit) {
                      onSaveBaseInfo()
                    } else {
                      setIsEdit(true)
                    }
                  }}
                >
                  {isEdit ? '保存' : '编辑'}
                </Button>
                {isEdit && (
                  <Button onClick={() => setIsEdit(false)}>{'取消'}</Button>
                )}
              </BtnsWrapper>
            </Form.Item>
          ) : null}
        </StyledForm>
      </BaseInfoWrapper>
    </EntryComponentsWrapper>
  )
}

const BaseInfoWrapper = styled.div`
  width: 750px;
  padding: 32px 20px;
  .ant-form-item-control {
    max-width: 488px;
  }
`

const BtnsWrapper = styled.div`
  margin: 30px 0 0 115px;
  display: flex;
  gap: 12px;
`

const StyledForm = styled(Form)`
  &&& {
    .ant-form-item {
      margin-bottom: 20px;
    }
    .ant-form-item-label {
      min-width: 198px;
      min-height: 30px;
    }
    .ant-form-item-label > label::after {
      margin-right: 0;
    }
    .ant-form-item-label > label {
      padding-right: 8px;
      white-space: normal;
      height: 100%;
      display: inline-block;
      vertical-align: top;
      line-height: 32px;
      & > .ant-form-item-tooltip {
        line-height: 35px;
      }
    }
    .anticon svg {
      color: ${(props) => props.theme.text._4};
    }
  }
`
